import {
	createContext,
	useState,
	useContext,
	Dispatch,
	SetStateAction,
	ReactNode,
	useEffect,
} from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from '../../util/Batch';
import { businessToday } from "../../util/businessClock";

import { addMoney, lineTotal } from "../../util/quantity";
export type ProductionRole = 'INPUT' | 'OUTPUT';
export type ProductionStatus = 'DRAFT' | 'RUNNING' | 'FINALIZED' | 'local';

export const isProductionStatus = (status: unknown): status is ProductionStatus =>
	status === 'DRAFT' || status === 'RUNNING' || status === 'FINALIZED' || status === 'local';

export interface ProductionLine {
	key: string;
	item: number;
	item_desc: string;
	role: ProductionRole;
	/** Expected (BOM × scale) */
	planned_qty: number;
	/** Actual (posted on finalize) */
	quantity: number;
	unit?: string;
	amount: number;
	isFractional?: boolean;
}

export interface ProductionTask extends BatchTask<ProductionLine> {
	depot: number;
	bom_id: number | null;
	bom_name: string;
	scale: number;
	status: ProductionStatus;
	components: ProductionLine[];
}

interface ContextProps {
	pendingTasks: ProductionTask[];
	setPendingTasks: Dispatch<SetStateAction<ProductionTask[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: ProductionTask | undefined;
	items: any[];
	setItems: Dispatch<SetStateAction<any[]>>;
	inputTotal: number;
	outputTotal: number;
	expectedInputTotal: number;
	expectedOutputTotal: number;
	isLocked: boolean;
}

const ProductionContext = createContext<ContextProps | undefined>(undefined);

interface ProviderProps {
	children: ReactNode;
}

export const ProductionProvider = ({ children }: ProviderProps) => {
	const [pendingTasks, setPendingTasks] = useState<ProductionTask[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);

	const activeTask = pendingTasks?.find((task) => task.active);
	const isLocked = activeTask?.status === 'FINALIZED';

	const inputTotal =
		activeTask?.components
			?.filter((c) => c.role === 'INPUT')
			.reduce((sum, c) => addMoney(sum, lineTotal(c.quantity, c.amount)), 0) || 0;

	const outputTotal =
		activeTask?.components
			?.filter((c) => c.role === 'OUTPUT')
			.reduce((sum, c) => addMoney(sum, lineTotal(c.quantity, c.amount)), 0) || 0;

	const expectedInputTotal =
		activeTask?.components
			?.filter((c) => c.role === 'INPUT')
			.reduce((sum, c) => addMoney(sum, lineTotal(c.planned_qty, c.amount)), 0) || 0;

	const expectedOutputTotal =
		activeTask?.components
			?.filter((c) => c.role === 'OUTPUT')
			.reduce((sum, c) => addMoney(sum, lineTotal(c.planned_qty, c.amount)), 0) || 0;

	const addNewTask = () => {
		const newTask: ProductionTask = {
			id: uuidv4(),
			active: true,
			date: businessToday(),
			reference: Util.getProductionNumber(),
			receiver: -1,
			depot: -1,
			bom_id: null,
			bom_name: '',
			scale: 1,
			status: 'local',
			description: '',
			components: [],
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return this.components
					.filter((c) => c.role === 'OUTPUT')
					.reduce((sum, c) => addMoney(sum, lineTotal(c.quantity, c.amount)), 0);
			},
		};

		setPendingTasks((prevTasks) =>
			prevTasks.map((task) => ({ ...task, active: false })).concat(newTask)
		);
	};

	useEffect(() => {
		if (fetchedTask) {
			// API may return RFC dates (e.g. "Tue, 11 Aug 2026 00:00:00 GMT");
			// always store ISO YYYY-MM-DD for Marshmallow Date fields.
			const rawDate = fetchedTask.date;
			const isoDate =
				typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate)
					? rawDate.slice(0, 10)
					: Util.formatISODate(new Date(rawDate));

			updateActiveTask(setPendingTasks, {
				...activeTask,
				id: fetchedTask.batch,
				date: isoDate,
				depot: fetchedTask.depot_id,
				receiver: fetchedTask.client_id,
				reference: fetchedTask.reference,
				description: fetchedTask.description || '',
				bom_id: fetchedTask.bom_id ?? null,
				bom_name: fetchedTask.bom_name || '',
				scale: fetchedTask.scale || 1,
				status: isProductionStatus(fetchedTask.status) ? fetchedTask.status : 'DRAFT',
				components: (fetchedTask.components || []).map((c: any, idx: number) => ({
					key: `${c.id || idx}-${c.item}`,
					item: c.item,
					item_desc: c.item_desc,
					role: c.role,
					planned_qty: c.planned_qty ?? c.quantity,
					quantity: c.quantity,
					unit: c.unit || '',
					amount: c.amount ?? 0,
					isFractional: c.is_fractional === true || c.IsFractional === true || items.find((i: any) => i.id === c.item)?.IsFractional === true,
				})),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchedTask, items]);

	const value: ContextProps = {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		addNewTask,
		activeTask,
		items,
		setItems,
		inputTotal,
		outputTotal,
		expectedInputTotal,
		expectedOutputTotal,
		isLocked,
	};

	return (
		<ProductionContext.Provider value={value}>{children}</ProductionContext.Provider>
	);
};

export const useProduction = () => {
	const context = useContext(ProductionContext);
	if (!context) {
		throw new Error('useProduction must be used within a ProductionProvider');
	}
	return context;
};
